/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { useUser } from '../hooks/UNSAFE_useUser';

import { BaseMenuProps } from './Menu';
import { useMenuCurrentKey } from './useMenuCurrentKey';
import {
  getPrevNext,
  keyElemExtractor,
  getLastVisible,
  getKeyboardCloseProps,
  getCloseSubmenuEvent,
  MenuCollectionItem,
  getNVisible,
  getMenuContainerFocusMethods,
  getMenuContainerMouseLeave,
  getMenuPointerDown
} from './menuUtils';
import { useMenuContext } from './useMenuContext';
import { MenuContainerContext } from './MenuContainerContext';
import { menuStyles } from './themes/MenuStyles.css';

type MenuContainerProps = Omit<
  BaseMenuProps,
  'isOpen' | 'anchorRef' | 'defaultPlacement' | 'offsetValue'
> & {
  display: 'sheet' | 'dropdown';
};

/*Component where general behavior is handled. Here we controled focus behavior, focus styling, submenu
opening behavior and current active item behavior. */
export const MenuContainer = ({
  children,
  'aria-label': ariaLabel,
  currentFocus = 'menu',
  display,
  closeSubmenu,
  menuId,
  menuLevel
}: MenuContainerProps) => {
  const { onClose } = useMenuContext();

  //Important to know direction so closeSubmenu is handled properly if needed.
  const { direction } = useUser();

  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  /*We share same behavior as legacy menu on mouseLeave that is:
  1. If we mouse leave a menuItem but we still on menuContainer and we are not hovering any other menuItem, we keep 
  the focus on the menuItem and hovering style.
  2. If we mouseLeave but next target is a submenu we open a submenu. 
  3. If we mouseLeave the container and next target is not a submenu, we reset current key and set the focus on the
  menuContainer.If we mouseLeaved a submenuItem, submenu is closed.
  General information used by menuItems:
  -wasMouseMoved: Used to don't allow mouseEnter callbacks being triggered if mouse was not moved already.
  -lastMouseLeaveValidatedRelatedTarget: We use bubbling nature of events to first check/validate the related target in onMouseLeave events of 
    menuItems. For menuItems we check if the related target is the menuContainer itself or a submenu. We validate there
    since at this level we don't have access to any submenu. Everytime mouseEnter triggers at menuContainer we have to compare if both
    relatedTargets are the same(menuContainer and last menuItem one), if not, this means mouse is now outside of the container.
  */
  const menuContainerGeneralInformation = useRef<{
    wasMouseMoved: boolean;
    lastMouseLeaveMenuItemdRelatedTarget: HTMLElement | null;
  }>({
    wasMouseMoved: false,
    lastMouseLeaveMenuItemdRelatedTarget: null
  });
  //Used so focus is handled just one time using currentFocus. Used combined with menuLevel due to main menu can't
  // change its focus via currentFocus prop.
  const initialFocusDone = useRef<boolean>(false);

  //Key of the active element on this menu container
  const [currentKey, setCurrentKey] = useState<string | undefined>();

  //State that represents if focused outline styles needs
  const [showFocusRing, setShowFocusRing] = useState(false);

  //Based on this we would include or not hovering styling. Same as legacy
  const [isContainerFocused, setIsContainerFocused] = useState(false);

  //Funtion triggered on blur. This is called since if focus is not anymore on the container we would need to do
  //some validation
  const blurFunction = () => {
    if (!menuContainerRef.current?.contains(document.activeElement)) {
      setShowFocusRing(false);
      setIsContainerFocused(false);
    }
  };

  const focusFunction = () => {
    setIsContainerFocused(true);
  };

  const mouseLeaveFunction = (e: MouseEvent) => {
    //We have to validate if new hover target was already validated correctly, if not this means mouse is now outside container
    if (
      !(e.relatedTarget as HTMLElement).isEqualNode(
        menuContainerGeneralInformation.current.lastMouseLeaveMenuItemdRelatedTarget
      )
    ) {
      //We reset current key when mouse is outside the container or any submenu. This is how legacy menu works
      changeKey({
        key: undefined,
        reason: 'mouseEnter'
      });
      //We focus menuContainer so keyboard navigation can be still used
      menuContainerRef.current?.focus();
    }
  };

  //Function used to change the currentKey
  const changeKey = useCallback((detail: MenuCollectionItem) => {
    setCurrentKey(detail.key);
    if (detail.reason === 'keyboard') {
      setShowFocusRing(true);
    } else {
      setShowFocusRing(false);
    }
  }, []);

  //This is used since if mouse overlaps space when menu is rendered, menuItem could trigger menuEnter,
  // which is not desirable.Once mouse was moved inside container, we could trigger mouseEnter events normally.
  const selfDestructiveMouseMoveEvent = useCallback(() => {
    const handler = (e: MouseEvent) => {
      const keyElem = keyElemExtractor(e.target as HTMLElement);
      const key = keyElem?.key;
      const elem = keyElem?.elem;
      //We will trigger this event until a menuItem is found. Usually this don't take to much to happen
      if (key && elem) {
        menuContainerGeneralInformation.current.wasMouseMoved = true;
        const evt = new MouseEvent('mouseenter');
        elem.dispatchEvent(evt);
        menuContainerRef.current?.removeEventListener('mousemove', handler);
      }
    };
    menuContainerRef.current?.addEventListener('mousemove', handler);
  }, []);

  const { currentKeyProps } = useMenuCurrentKey(
    getPrevNext(menuContainerRef.current, true, currentKey),
    getPrevNext(menuContainerRef.current, false, currentKey),
    () => getNVisible(menuContainerRef.current, 0),
    () => getLastVisible(menuContainerRef.current),
    changeKey
  );

  useEffect(() => {
    if (menuLevel === 'main' && initialFocusDone.current) return;
    initialFocusDone.current = true;
    const menuContainer = menuContainerRef.current;
    let timer: NodeJS.Timeout | undefined;
    /*TODO: There are some cases where is not safe to focus since 
    visibility initially is hidden at the Floating component at the initial render. Need a way to solve this in a 
    less dirty way (we are now using timeouts). More details on the descripton of JET-57353 */
    if (currentFocus !== 'none' && menuContainer) {
      if (currentFocus === 'menu') {
        changeKey({ key: undefined, reason: 'click' });
        const focusMenuContainer = () => {
          menuContainer.focus();
        };
        if (display === 'sheet') {
          focusMenuContainer();
        } else {
          // At this point floating has visibility set as hidden, so also menuContainer. Settimeout added so menuContainer
          // is visible at that point and can be focused.
          timer = setTimeout(() => {
            focusMenuContainer();
          }, FOCUS_SHIFT_TIMEOUT);
        }
      } else if (currentFocus === 'firstItem') {
        const firstItem = getNVisible(menuContainerRef.current, 0);
        if (firstItem) {
          changeKey({ key: firstItem, reason: 'keyboard' });
        }
      } else if (currentFocus === 'secondItem') {
        //This is just used in sheet menu, so no need to set a timeout
        const item = getNVisible(menuContainerRef.current, 1);
        if (item) {
          changeKey({ key: item, reason: 'keyboard' });
        }
      }
    }
    return () => {
      //We have to clean timers, because comp can be unmounted after finishing timeouts
      clearTimeout(timer);
    };
  }, [changeKey, currentFocus, menuLevel, display]);

  useEffect(() => {
    selfDestructiveMouseMoveEvent();
  }, [selfDestructiveMouseMoveEvent]);

  return (
    <div
      {...(menuId && { id: menuId })}
      ref={menuContainerRef}
      tabIndex={!currentKey && (menuLevel === 'main' || display === 'sheet') ? 0 : -1}
      role="menu"
      aria-label={ariaLabel}
      class={classNames([
        menuStyles.menuContainerStyle,
        menuStyles[`${display}MenuContainerStyle`]
      ])}
      {...mergeProps(
        currentKeyProps,
        getKeyboardCloseProps(onClose, menuLevel),
        getCloseSubmenuEvent(direction, closeSubmenu),
        getMenuContainerFocusMethods(blurFunction, focusFunction),
        getMenuContainerMouseLeave(mouseLeaveFunction),
        getMenuPointerDown()
      )}>
      <MenuContainerContext.Provider
        value={{
          /*We use this ref on dropdownSubmenu since unhover only close the menu if menuContainer ref does not include
          the new element that receives the hovering */
          menuContainerRef,
          currentKey,
          showFocusRing,
          changeKey,
          menuContainerGeneralInformation,
          isContainerFocused
        }}>
        {children}
      </MenuContainerContext.Provider>
    </div>
  );
};

//Temporal constant, will probably be removed with JET-57353
const FOCUS_SHIFT_TIMEOUT = 0;
